import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import { stringRequiredEqualsValidationScheme } from '@utils/validation';
import { useFormik } from 'formik';
import React from 'react';
import { object } from 'yup';

interface IDeleteConfirmationDialogProps {
	onDelete: () => void;
	onCancel: () => void;
	title: string;
	question: React.ReactNode;
	extraVerification: boolean;
	name?: string;
	inputHeader?: string;
	inputText?: string;
}

const DeleteConfirmationDialog = ({
	onDelete,
	onCancel,
	title,
	question,
	extraVerification,
	name,
	inputHeader,
	inputText
}: IDeleteConfirmationDialogProps) => {
	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			deleteVal: ''
		},
		validationSchema: extraVerification
			? object({
					deleteVal: stringRequiredEqualsValidationScheme(name!)
			  })
			: null,
		onSubmit: () => onDelete(),
		validateOnMount: true
	});

	return (
		<Dialog open>
			<DialogTitle>
				<Box display="flex" flexDirection="row" alignItems="center">
					<DeleteForeverIcon color="error" />
					<Typography variant="h6" pl={1}>
						{title}
					</Typography>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={3} p={1} maxWidth="450px">
					<Grid item xs={12}>
						<Typography variant="body1" whiteSpace="break-spaces">
							{question}
						</Typography>
					</Grid>
					{extraVerification && (
						<Grid item xs={12}>
							<Typography variant="body2" pb={2} pt={1}>
								{inputHeader}
							</Typography>
							<TextFieldValidation
								id="deleteVal"
								formik={formik}
								focused
								fullWidth
								autoFocus
								required
								label={inputText}
								placeholder={inputText}
							/>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					data-testid="delete-confirmation-dialog-button-cancel"
					onClick={onCancel}
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					data-testid="delete-confirmation-dialog-button"
					variant="contained"
					color="error"
					loading={formik.isSubmitting}
					disabled={extraVerification && !formik.isValid}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.delete')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

DeleteConfirmationDialog.defaultProps = {
	name: '',
	inputHeader: '',
	inputText: ''
};

export default DeleteConfirmationDialog;
